import React, { useState } from "react";
import RawMaterialHistorySearch from "./RawMaterialHistory-Search";
import RawMaterialHistoryDatatable from "./RawMaterialHistory-Datatable";
import RawMaterialHistoryExport from "./RawMaterialHistory-Export";
import { getRawMaterialUtilizationSummary } from "../../services/RawMaterials/RawMaterialService";

export interface RawMaterialHistoryRow {
  id: number;
  posId: number;
  vendorId: number;
  rawMaterialId: number;
  invoiceNumber: string;
  purchaseDate: string;
  invoicedQuantity: number;
  basicAmount: number;
  taxAmount: number;
  totalAmount: number;
  acceptedQuantity: number | null;
  rejectedQuantity: number;
  utilizedQuantity: number;
  utilizedQuantityDisplay: string;
  invoicedRemainingQty: number;
  acceptedRemainingQty: number;
  remainingQty: number;
  vendorName: string;
  rawMaterialName: string;
  posAddress: string;
}

interface SearchFilters {
  query: string;
  fromDate?: Date | null;
  toDate?: Date | null;
  vendorId?: number | null;
  rawMaterialIds?: number[] | null;
  utilizedQtyRange?: [number, number];
  sortBy?: string | null;
  sortDirection?: string | null;
}

const RawMaterialHistoryMain: React.FC = () => {
  const [historyRows, setHistoryRows] = useState<RawMaterialHistoryRow[]>([]);
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(0);
  const [hasSearched, setHasSearched] = useState(false);

  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({
    query: "",
    fromDate: null,
    toDate: null,
    vendorId: null,
    rawMaterialIds: null,
    utilizedQtyRange: [0, 0],
    sortBy: null,
    sortDirection: null,
  });

  const formatDateForAPI = (date: Date | null | undefined): string | null => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const handleUnitChange = (unitIds: number[]) => {
    setSelectedUnitIds(unitIds);
    if (unitIds.length === 0) {
      setHistoryRows([]);
      setTotalRecords(0);
      setPageNumber(1);
      setCurrentFilters({
        query: "",
        fromDate: null,
        toDate: null,
        vendorId: null,
        rawMaterialIds: null,
        utilizedQtyRange: [0, 100],
        sortBy: null,
        sortDirection: null,
      });
    }
  };

  const handleReset = () => {
    setHistoryRows([]);
    setTotalRecords(0);
    setSelectedUnitIds([]);
    setPageNumber(1);
    setSortField(null);
    setSortOrder(0);
    setHasSearched(false); 
    setCurrentFilters({
      query: "",
      fromDate: null,
      toDate: null,
      vendorId: null,
      rawMaterialIds: null,
      utilizedQtyRange: [0, 100],
      sortBy: null,
      sortDirection: null,
    });
  };

  const handleSearch = async (
    query: string,
    fromDate?: Date | null,
    toDate?: Date | null,
    vendorId?: number | null,
    rawMaterialIds?: number[] | null,
    utilizedQtyRange?: [number, number],
    sortBy?: string | null,
    sortDirection?: string | null,
    page: number = 1,
    size: number = pageSize
  ) => {
    if (selectedUnitIds.length === 0) return;
    try {
      setLoading(true);
      const response = await getRawMaterialUtilizationSummary({
        posIds: selectedUnitIds,
        vendorId: vendorId ?? null,
        rawMaterialIds: rawMaterialIds ?? [],
        startDate: formatDateForAPI(fromDate),
        endDate: formatDateForAPI(toDate),
        minUtilizedQty:
          utilizedQtyRange && (utilizedQtyRange[0] !== 0 || utilizedQtyRange[1] !== 0)
            ? utilizedQtyRange[0]
            : null,
        maxUtilizedQty:
          utilizedQtyRange && (utilizedQtyRange[0] !== 0 || utilizedQtyRange[1] !== 0)
            ? utilizedQtyRange[1]
            : null,
        pageNumber: page,
        pageSize: size,
        sortBy: sortBy ?? null,
        sortDirection: sortDirection ?? null,
      });

      setHistoryRows(response.data);
      setTotalRecords(response.totalRecords);
      setPageNumber(page);
      setPageSize(size);
      setHasSearched(true); 

      setCurrentFilters({
        query,
        fromDate,
        toDate,
        vendorId,
        rawMaterialIds,
        utilizedQtyRange,
        sortBy,
        sortDirection,
      });
    } catch (error) {
      console.error("Error fetching raw material history:", error);
      setHistoryRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (e: any) => {
    const newPage = Math.floor(e.first / e.rows) + 1;
    handleSearch(
      currentFilters.query,
      currentFilters.fromDate,
      currentFilters.toDate,
      currentFilters.vendorId,
      currentFilters.rawMaterialIds,
      currentFilters.utilizedQtyRange,
      currentFilters.sortBy,
      currentFilters.sortDirection,
      newPage,
      e.rows
    );
  };

  const handleSort = (e: any) => {
    const direction = e.sortOrder === 1 ? "asc" : "desc";
    const sortBy = e.sortField;
    setSortField(sortBy);
    setSortOrder(e.sortOrder);
    handleSearch(
      currentFilters.query,
      currentFilters.fromDate,
      currentFilters.toDate,
      currentFilters.vendorId,
      currentFilters.rawMaterialIds,
      currentFilters.utilizedQtyRange,
      sortBy,
      direction,
      1,
      pageSize
    );
  };

  return (
    <div className="p-4 w-full">
      <div className="flex justify-between items-center ">
        <h2 className="text-green-800 font-bold text-xl">
          Raw Material Utilization Summary
        </h2>
        <div className="ml-auto">
          <RawMaterialHistoryExport
            selectedUnitIds={selectedUnitIds}
            filters={currentFilters}
            disabled={!hasSearched}   
          />
        </div>
      </div>

      {/* Search Section */}
      <div className="w-full bg-white border border-gray-300 p-3 rounded-lg shadow-sm">
        <RawMaterialHistorySearch
          onUnitChange={handleUnitChange}
          onSearch={(query, fromDate, toDate, vendorId, rawMaterialIds, utilizedQtyRange) =>
            handleSearch(
              query,
              fromDate,
              toDate,
              vendorId,
              rawMaterialIds,
              utilizedQtyRange,
              currentFilters.sortBy,
              currentFilters.sortDirection,
              1,
              pageSize
            )
          }
          onReset={handleReset}
          setLoading={setLoading}
        />
      </div>

      {/* DataTable Section */}
      <div className="w-full">
       <RawMaterialHistoryDatatable
  data={historyRows}
  loading={loading}
  totalRecords={totalRecords}
  page={pageNumber - 1}
  rows={pageSize}
  onPage={handlePageChange}
  onSort={handleSort}
  sortField={sortField}
  sortOrder={sortOrder}
  selectedUnitId={selectedUnitIds.length > 0 ? selectedUnitIds.join(",") : null}
  hasSearched={hasSearched}
/>

      </div>
    </div>
  );
};

export default RawMaterialHistoryMain;
